import { useState } from "react";
import { fixedColumns as defaultFixedColumns } from "./tableConfigs";
import { renderTableCaption, renderTableHead } from "../../../utils/tableUtils";
import { Occurrence, OccurrenceStep, OccurrenceStepLog } from "../../../models/occurrence";
import {
  getOccurrenceStepKeyOfValue,
  getOccurrenceStepLabelByStepValue,
  OccurrenceStepEnum,
  OccurrenceStepLabelEnum,
} from "../../../enums/occurrenceStep";
import { formatMilisecondsInDaysHoursMinutes } from "../../../utils/dateUtils";
import useTableScroll from "../../../hooks/useTableScroll";
import { ColumnConfig } from "../dialogs/tableSettingsDialog";
import "../styles.scss";
import { useMediaQuery } from "@mui/material";

const renderCellContent = (
  column: string,
  occurrence: Occurrence,
  loading: boolean
) => {
  const nowForRender = Date.now();
  const getDurationMs = (start?: Date | null, end?: Date | null) => {
    if (!start) return 0;
    const endMs = end ? end.getTime() : nowForRender;
    return Math.max(0, endMs - start.getTime());
  };

  if (loading) {
    return <div className="animate-pulse w-3/4 h-4 bg-gray-300 dark:bg-neutral-700 rounded" />;
  }

  if (column === "criticidade") {
    const criticalityColorMap = {
      5: "bg-red-500",
      4: "bg-orange-400",
      3: "bg-yellow-500",
      2: "bg-green-500",
      1: "bg-blue-500",
    };

    const criticalityLabelMap = {
      5: "Emergência",
      4: "Muito urgente",
      3: "Urgente",
      2: "Pouco urgente",
      1: "Não urgente",
    };

    return (
      <div className="flex items-center gap-4">
        <span
          className={`w-[9px] h-[9px] rounded-full ${criticalityColorMap[
            occurrence.criticality as keyof typeof criticalityColorMap
            ]
            } inline-block`}
        />
        <span>
          {
            criticalityLabelMap[
            occurrence.criticality as keyof typeof criticalityLabelMap
            ]
          }
        </span>
      </div>
    );
  }

  if (column === "veiculo") {
    return (
      <div className="flex flex-col text-[#2C3E50] dark:text-neutral-100 leading-tight">
        <div className="truncate font-medium">
          {occurrence.vehicleName ?? "-"}
        </div>
        <div className="text-[#556677] dark:text-neutral-400 truncate">
          {occurrence.vehicleModel ?? "-"}
        </div>
      </div>
    );
  }

  if (column === "chassi") {
    return (
      <div className="flex flex-col text-[#2C3E50] dark:text-neutral-100 leading-tight">
        <span>{occurrence.vehicleChassis}</span>
      </div>
    );
  }

  if (column === "odometro") {
    return (
      <div className="flex flex-col text-[#2C3E50] dark:text-neutral-100 leading-tight">
        <span>{occurrence.vehicleOdometer} KM</span>
      </div>
    );
  }

  if (column === "horimetro") {
    return (
      <div className="flex flex-col text-[#2C3E50] dark:text-neutral-100 leading-tight">
        <span>{occurrence.vehicleHourmeter}</span>
      </div>
    );
  }

  if (column === "fixedTempo") {
    // Função auxiliar para calcular o tempo de uma etapa considerando múltiplas passagens
    const getStepTimeWithMultiplePassages = (step: OccurrenceStep) => {
      if (!step?.dtStart) return 0;

      const logs = step.logs ?? [];
      
      // Se não há logs, calcula normalmente
      if (logs.length === 0) {
        return getDurationMs(step.dtStart, step.dtEnd);
      }

      // Filtra logs que têm startDate e endDate (passagens concluídas)
      const completedLogs = logs.filter((log: OccurrenceStepLog | undefined) => 
        log?.startDate && log?.endDate
      );

      // Se há múltiplas passagens concluídas, calcula do primeiro início até a última conclusão
      if (completedLogs.length > 1) {
        const firstStartDate = completedLogs.reduce((earliest: Date | null, log: OccurrenceStepLog) => {
          const startDate = new Date(log.startDate);
          return !earliest || startDate < earliest ? startDate : earliest;
        }, null);
        
        const lastEndDate = completedLogs.reduce((latest: Date | null, log: OccurrenceStepLog) => {
          const endDate = new Date(log.endDate);
          return !latest || endDate > latest ? endDate : latest;
        }, null);

        if (firstStartDate && lastEndDate) {
          return lastEndDate.getTime() - firstStartDate.getTime();
        }
      }

      // Se há apenas uma passagem ou está em progresso, soma as durações individuais
      const totalTime = logs.reduce((acc: number, log: OccurrenceStepLog | undefined) => {
        if (log?.startDate && log?.endDate) {
          return acc + (new Date(log.endDate).getTime() - new Date(log.startDate).getTime());
        } else if (log?.startDate) {
          return acc + (nowForRender - new Date(log.startDate).getTime());
        }
        return acc;
      }, 0);

      return totalTime;
    };

    const totalMs =
      occurrence.steps
        ?.filter((step) => step.dtStart)
        .reduce(
          (acc, step) => acc + getStepTimeWithMultiplePassages(step),
          0
        ) ?? 0;

    return (
      <div className="flex items-center gap-4 text-[#2C3E50] dark:text-neutral-100 leading-tight">
        <span className="rioglyph rioglyph-time-alt text-size-18" />
        <span>{formatMilisecondsInDaysHoursMinutes(totalMs)}</span>
      </div>
    );
  }

  const stepFound = occurrence.steps?.find(
    (step) =>
      step.step ===
      OccurrenceStepEnum[column as keyof typeof OccurrenceStepEnum]
  );

  if (!stepFound) return <span className="px-2 py-[2px]">Não iniciado</span>;

  const [status, time] = [
    stepFound.dtEnd ? "Concluído" : "Em progresso",
    stepFound.dtStart
      ? formatMilisecondsInDaysHoursMinutes(
        getDurationMs(stepFound.dtStart, stepFound.dtEnd)
      )
      : "",
  ];

  if (column === "tempo") {
    return (
      <span className="flex items-center gap-2">
        <span className="rioglyph rioglyph-time-alt text-size-18" />
        {time}
      </span>
    );
  }

  const badgeColor =
    status === "Concluído"
      ? "bg-[#ECFDF3] text-[#067647] border border-[#ABEFC6] dark:bg-green-900/40 dark:text-green-300 dark:border-green-800"
      : status === "Em progresso"
        ? "bg-[#F0F9FF] text-[#026AA2] border border-[#B9E6FE] dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800"
        : "bg-gray-200 text-gray-600 dark:bg-neutral-700 dark:text-neutral-300";

  return (
    <div className="flex gap-[5px]">
      <span
        className={`px-2 py-[2px] rounded-full font-medium w-[100px] text-center ${badgeColor}`}
      >
        {status}
      </span>
      {time && (
        <span className="flex items-center gap-1 font-semibold">
          <span className="rioglyph rioglyph-time-line" />
          {time}
        </span>
      )}
    </div>
  );
};

interface TableKanbanViewProps {
  occurrenceList: Occurrence[];
  loading: boolean;
  occurrenceStepAvailable: string[];
}

const TableKanbanView = (props: TableKanbanViewProps) => {
  const { scrolledClass, scrollableTableRef } = useTableScroll();
  const [fixedColumnsState] = useState<ColumnConfig[]>(
    defaultFixedColumns.map((col) => ({ ...col, visible: true }))
  );
  const visibleFixedColumns = fixedColumnsState.filter((c) => c.visible);


  const isResponsive = useMediaQuery("(max-width:1024px)");

  if (isResponsive) {
    return (
      <div className="tables-row overflow-x-auto w-full">
        <table className="table table-head-filled table-bordered min-w-full">
          <thead>
            <tr>
              {visibleFixedColumns.map((column) =>
                renderTableHead(column.value, column.label, "veiculo", () => {})
              )}
              {props.occurrenceStepAvailable.map((column) =>
                renderTableHead(
                  getOccurrenceStepKeyOfValue(column),
                  OccurrenceStepLabelEnum[
                    column as keyof typeof OccurrenceStepLabelEnum
                  ],
                  "veiculo",
                  () => {}
                )
              )}
            </tr>
          </thead>
          <tbody>
            {(props.loading ? new Array(10).fill(0) : props.occurrenceList).map(
              (occurrence, occurrenceIndex) => (
                <tr key={occurrenceIndex}>
                  {visibleFixedColumns.map((column, colIndex) => (
                    <td
                      key={`${column.value}-${occurrenceIndex}-${colIndex}`}
                      data-field={column.label}
                    >
                      {renderCellContent(column.value, occurrence, props.loading)}
                    </td>
                  ))}
                  {props.occurrenceStepAvailable.map((column, colIndex) => (
                    <td
                      key={`${column}-${occurrenceIndex}-${colIndex}`}
                      data-field={getOccurrenceStepLabelByStepValue(column)}
                    >
                      {renderCellContent(column, occurrence, props.loading)}
                    </td>
                  ))}
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div id="TableCommonDemo">
      {/* Botão temporário para abrir o modal de configuração de colunas da tabela fixa */}

      {/* <button
        onClick={() => setModalOpen(true)}
        className="mb-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
      >
        Configurar colunas
      </button>

      <TableSettingsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        columns={fixedColumnsState}
        setColumns={setFixedColumnsState}
      /> */}

      <div className="tables-row">
        <div className="tables-row__fixed w-[770px]">
          <table className="table table-layout-fixed table-bordered">
            <colgroup>
              {visibleFixedColumns.map((column) =>
                renderTableCaption(column.value, column.details)
              )}
            </colgroup>
            <thead>
              <tr>
                {visibleFixedColumns.map((column) =>
                  renderTableHead(
                    column.value,
                    column.label,
                    "veiculo",
                    () => { }
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {(props.loading
                ? new Array(10).fill(0)
                : props.occurrenceList
              ).map((occurrence, occurrenceIndex) => (
                <tr key={occurrenceIndex}>
                  {visibleFixedColumns.map((column, colIndex) => (
                    <td
                      key={`${column.value}-${occurrenceIndex}-${colIndex}`}
                      data-field={column.label}
                      style={
                        column.value !== "veiculo"
                          ? { whiteSpace: "nowrap" }
                          : {}
                      }
                    >
                      {renderCellContent(
                        column.value,
                        occurrence,
                        props.loading
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div
          className={`scrollabe-table ml-[770px] ${scrolledClass ? "scrolled-ativo" : ""
            }`}
          ref={scrollableTableRef}
          data-scrolled={scrolledClass}
        >
          <table
            className="table table-layout-fixed table-column-overflow-hidden table-bordered table-head-filled"
            style={{ width: `${props.occurrenceStepAvailable.length * 140}px` }}
          >
            <colgroup>
              {props.occurrenceStepAvailable.map((column) =>
                renderTableCaption(column, {
                  width: 200,
                  defaultWidth: 200,
                  maxWidth: 200,
                })
              )}
            </colgroup>
            <thead>
              <tr>
                {props.occurrenceStepAvailable.map((column) =>
                  renderTableHead(
                    getOccurrenceStepKeyOfValue(column),
                    OccurrenceStepLabelEnum[column as keyof typeof OccurrenceStepLabelEnum],
                    "veiculo",
                    () => { }
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {(props.loading
                ? new Array(10).fill(0)
                : props.occurrenceList
              ).map((occurrence, occurrenceIndex) => (
                <tr key={occurrenceIndex}>
                  {props.occurrenceStepAvailable.map(
                    (column, colIndex) => (
                      <td
                        key={`${column}-${occurrenceIndex}-${colIndex}`}
                        data-field={getOccurrenceStepLabelByStepValue(column)}
                      >
                        {renderCellContent(column, occurrence, props.loading)}
                      </td>
                    )
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TableKanbanView;
